import React, { useEffect, useRef, useState } from "react";
import * as Icons from "react-feather";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import SKAlerts, { CreateAlert } from "../../shared/common-alerts/SKAlerts";
import { ReactComponent as SortArrow } from "../../shared/img/chevron-up-down.svg";
import TaskingCommentHistory from "./TaskingCommentHistory";
import MultiSelectDropdown from "../../shared/common-dropdown/MultiSelectDropdown";
import PriorityGridDropdown from "../../shared/common-dropdown/PriorityGridDropdown";
import StatusGridDropdown from "../../shared/common-dropdown/StatusGridDropdown";
import UserGridDropdown from "../../shared/common-dropdown/UserGridDropdown";
import { getApiBaseUrl } from "../../utils/apiConfig";
import { getStorageValues } from "../../shared/CommonFunctions";

function ManageTaskGrid(props) {
  const [filterText, setFilterText] = React.useState("");
  const [alert, setAlert] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(
    props.DefaultTaskId != null ? Number(props.DefaultTaskId) : null
  );
  const commentRef = useRef(null);
  const [getStatusOptions, setStatusOptions] = useState([]);
  const [getPriorityOptions, setPriorityOptions] = useState([]);
  const [getAssignedToOptions, setAssignedToOptions] = useState([]);
  const [getTitleOptions, setTitleOptions] = useState([]);
  const [getTaskKeyOptions, setTaskKeyOptions] = useState([]);
  const [getCompanyTypeOptions, setCompanyTypeOptions] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [editableCell, setEditableCell] = useState({
    rowId: null,
    column: null,
  });
  const [taskRows, setTaskRows] = useState(props.TaskData || []);
  const [filterValueStrings, setFilterValueStrings] = useState({});
  const [showCompleted, setShowCompleted] = useState(false);
  const [showFollowupRequired, setShowFollowupRequired] = useState(false);
  const [showFollowupTab, setShowFollowupTab] = useState(false);

 

  const filteredRows =
    taskRows?.filter((item) => {
      const matchesText =
        !filterText ||
        item.Title?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.Description?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.TaskKey?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.ClientName?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.ClientCompanyTypeName?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.ClientCompanyName?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.Priority?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.AssignedTo?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.Status?.toLowerCase().includes(filterText.toLowerCase());

      // Filter by completed status
      const matchesCompletedFilter = showCompleted || item.Status?.toLowerCase() !== 'completed';

      // Apply advanced filters (Priority, Status, etc.)
      const matchesFilters = Object.entries(filterValueStrings).every(
        ([key, valueStr]) => {
          if (!valueStr) return true; // No filter on this field
          const valueArray = valueStr.split(",").map((v) => v.trim());

          switch (key) {
            case "Priority":
              return valueArray.includes(String(item.PriorityId));
            case "Status":
              return valueArray.includes(String(item.StatusId));
            case "AssignedTo": {
              const assignedToId = item.AssignedToId ?? item.UserId ?? item.Id;
              const assignedToName = (item.AssignedTo || '').toString().toLowerCase();
              return valueArray.some((selectedVal) => {
                const normalized = selectedVal.toString().toLowerCase();
                return normalized === String(assignedToId) || normalized === assignedToName;
              });
            }
            case "Title":
              return valueArray.includes(String(item.TitleId));
            case "TaskKey":
              return valueArray.includes(String(item.TaskId));
            case "CompanyType":
                return valueArray.includes(String(item.ClientCompanyTypeId));
            default:
              return true;
          }
        }
      );
      const matchesFollowupRequired =
        !showFollowupRequired ||
        (item.NextFollowupDate &&
          moment(item.NextFollowupDate).startOf("day").isSameOrBefore(moment().startOf("day")));

      return matchesText && matchesFilters && matchesCompletedFilter && matchesFollowupRequired;
    }) || [];

  const handleRowExpandToggled = (expanded, row) => {
    // Toggle: if already expanded, collapse it
    setExpandedRowId((prev) => (prev === row.TaskId ? null : row.TaskId));
    setShowFollowupTab(false);
  };

  const handleNextFollowupUpdate = (taskId, nextFollowupDate) => {
    setTaskRows((prev) =>
      prev.map((task) =>
        task.TaskId === taskId
          ? { ...task, NextFollowupDate: nextFollowupDate }
          : task
      )
    );
  };
  const handleFilterSelected = (selectedOptions, filterKey) => {
    const valueString = Array.isArray(selectedOptions)
      ? selectedOptions.map((opt) => opt.value).join(",")
      : selectedOptions?.value?.toString() || "";

    setFilterValueStrings((prev) => ({
      ...prev,
      [filterKey]: valueString,
    }));
    // Update selected filter values
    setFilterValues((prev) => ({
      ...prev,
      [filterKey]: selectedOptions,
    }));
  };
  const isRowExpanded = (row) => Number(row.TaskId) === Number(expandedRowId);
  const handleDropdownChange = (e, row) => {
    if (!e || !e.value || !e.name || !row) return;

    const updatedRows = taskRows.map((task) =>
      task.TaskId === row.TaskId
        ? {
            ...task,
            [e.name]: e.label, // update display value
            [e.name + "Id"]: e.value, // update ID (e.g., PriorityId)
          }
        : task
    );
    setTaskRows(updatedRows);

    const taskUpdateDetails = {
      UserID: getStorageValues("userId"),
      TaskId: row.TaskId,
      FieldId: e.value,
      Description: row.Description,
      UpdateField: e.name === "AssignedTo" ? "User" : e.name,
    };
    updateTask(taskUpdateDetails);
  };

  async function updateTask(data) {
    try {
      const response = await axios.post(getApiBaseUrl() + "/updateTask", data);
      if (response.status === 200) {
        // const resEmail = await axios.post(
        //   getApiBaseUrl() + "/send-email",
        //   emailDetails
        // );
        // if(resEmail === 200){
        //   CreateAlert("email sent!", "Success", setAlert);
        // }
        if (commentRef.current) {
          commentRef.current.refreshHistory();
        }
        CreateAlert("Task updated successfully!", "Success", setAlert);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const columns = [
    {
      name: (
        <div className="titlename">
          <span>Key </span>
        </div>
      ),
      selector: (row) => row.TaskKey,
      cell: (row) => <span title={row.TaskKey}>{row.TaskKey}</span>,
      sortFunction: (rowA, rowB) => {
        const numA = parseInt(rowA.TaskKey.split("-")[1], 10);
        const numB = parseInt(rowB.TaskKey.split("-")[1], 10);
        return numA - numB;
      },
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Created Date</span>
        </div>
      ),
      selector: (row) => row.CreatedDate,
      cell: (row) => {
        const formattedDate = row.CreatedDate ? moment(row.CreatedDate).format('DD/MM/YYYY') : '-';
        return (
          <span title={row.CreatedDate}>{formattedDate}</span>
        );
      },
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Title </span>
        </div>
      ),
      selector: (row) => row.Title,
      cell: (row) => <span title={row.Title}>{row.Title}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Client Name </span>
        </div>
      ),
      selector: (row) => row.ClientName,
      cell: (row) => <span title={row.ClientName}>{row.ClientName}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Company Type</span>
        </div>
      ),
      selector: (row) => row.ClientCompanyTypeName,
      cell: (row) => (
        <span title={row.ClientCompanyTypeName}>{row.ClientCompanyTypeName}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Client Company </span>
        </div>
      ),
      selector: (row) => row.ClientCompanyName,
      cell: (row) => (
        <span title={row.ClientCompanyName}>{row.ClientCompanyName}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Next Followup</span>
        </div>
      ),
      selector: (row) => row.NextFollowupDate,
      cell: (row) => {
        const formattedDate = row.NextFollowupDate ? moment(row.NextFollowupDate).format('DD/MM/YYYY') : '';
        return (
          <span title={row.NextFollowupDate}>{formattedDate}</span>
        );
      },
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Priority </span>
        </div>
      ),
      selector: (row) => row.Priority,
      cell: (row) => {
        const isEditing =
          editableCell.rowId === row.TaskId &&
          editableCell.column === "Priority";
        return isEditing ? (
          <PriorityGridDropdown
            key={`priority-${row.TaskId}`}
            autoFocus={true}
            showLabel={false}
            selectedValue={row.PriorityId}
            onChangeDropdown={(e) => {
              handleDropdownChange(e, row);
              setEditableCell({ rowId: null, column: null }); // Close dropdown after change
            }}
            onBlur={() => setEditableCell({ rowId: null, column: null })}
            openMenuOnFocus={true}
            openMenuOnClick={true}
          />
        ) : (
          <div
            className="editable-cell-with-icon"
            onClick={() =>
              setEditableCell({ rowId: row.TaskId, column: "Priority" })
            }
            style={{ cursor: "pointer", height: "100%", width: "100%" }}
          >
            <div className="cell-content">{row.Priority}</div>
            <span className="edit-icon">
              <Icons.Edit size={12} color="#1261a0" />
            </span>
          </div>
        );
      },
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Assigned To </span>
        </div>
      ),
      selector: (row) => row.AssignedTo,
      cell: (row) => {
        const isEditing =
          editableCell.rowId === row.TaskId &&
          editableCell.column === "AssignedTo";
        return isEditing ? (
          <UserGridDropdown
            key={`user-${row.TaskId}`}
            autoFocus={true}
            showLabel={false}
            selectedValue={row.AssignedToId}
            onChangeDropdown={(e) => {
              handleDropdownChange(e, row);
              setEditableCell({ rowId: null, column: null }); // Close dropdown after change
            }}
            onBlur={() => setEditableCell({ rowId: null, column: null })}
            openMenuOnFocus={true}
            openMenuOnClick={true}
          />
        ) : (
          <div
            className="editable-cell-with-icon"
            onClick={() =>
              setEditableCell({ rowId: row.TaskId, column: "AssignedTo" })
            }
            style={{ cursor: "pointer", height: "100%", width: "100%" }}
          >
            <div className="cell-content">{row.AssignedTo}</div>
            <span className="edit-icon">
              <Icons.Edit size={12} color="#1261a0" />
            </span>
          </div>
        );
      },
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Status </span>
        </div>
      ),
      selector: (row) => row.Status,
      cell: (row) => {
        const isEditing =
          editableCell.rowId === row.TaskId && editableCell.column === "Status";
        return isEditing ? (
          <StatusGridDropdown
            key={`status-${row.TaskId}`}
            autoFocus={true}
            showLabel={false}
            selectedValue={row.StatusId}
            onChangeDropdown={(e) => {
              handleDropdownChange(e, row);
              setEditableCell({ rowId: null, column: null }); // Close dropdown after change
            }}
            onBlur={() => setEditableCell({ rowId: null, column: null })}
            openMenuOnFocus={true}
            openMenuOnClick={true}
          />
        ) : (
          <div
            className="editable-cell-with-icon"
            onClick={() =>
              setEditableCell({ rowId: row.TaskId, column: "Status" })
            }
            style={{ cursor: "pointer", height: "100%", width: "100%" }}
          >
            <div className="cell-content">{row.Status}</div>
            <span className="edit-icon">
              <Icons.Edit size={12} color="#1261a0" />
            </span>
          </div>
        );
      },
      sortable: true,
      sortIcon: <SortArrow />,
    },
  ];
  function onChangeSearch(e) {
    setFilterText(e.target.value);
  }
  function onFilterByClick(e) {
    const filterName = e.currentTarget.name;
    if (!activeFilters.includes(filterName)) {
      setActiveFilters((prev) => [...prev, filterName]);
      fetchOptions(filterName);
    }
  }
  useEffect(() => {
    setTaskRows(props.TaskData || []);
  }, [props.TaskData]);

  useEffect(() => {
    if (props.DefaultTaskId == null) return;
    setExpandedRowId(Number(props.DefaultTaskId));
    setShowFollowupTab(!!props.DefaultShowFollowupTab);
  }, [props.DefaultTaskId, props.DefaultShowFollowupTab]);

  useEffect(() => {
    if (props.DefaultTaskId != null) return;
    if (!props.DefaultTaskKey) return;
    if (!Array.isArray(taskRows) || taskRows.length === 0) return;

    const match = taskRows.find(
      (t) => String(t.TaskKey).trim() === String(props.DefaultTaskKey).trim()
    );
    if (match?.TaskId != null) {
      setExpandedRowId(Number(match.TaskId));
      setShowFollowupTab(!!props.DefaultShowFollowupTab);
    }
  }, [props.DefaultTaskId, props.DefaultTaskKey, props.DefaultShowFollowupTab, taskRows]);

  useEffect(() => {
    if (props.DefaultStatusFilter) {
      // Ensure options are loaded
      if (getStatusOptions.length === 0) {
        fetchOptions("Status");
      }
    }
    if (props.DefaultCompanyTypeFilter) {
      // Ensure options are loaded
      if (getCompanyTypeOptions.length === 0) {
        fetchOptions("CompanyType");
      }
    }
    if (props.DefaultTaskId && getTaskKeyOptions.length === 0) {
      fetchOptions("TaskKey");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.DefaultStatusFilter, 
    props.DefaultCompanyTypeFilter, 
    props.DefaultTaskId
  ]);

  useEffect(() => {
    if (props.DefaultStatusFilter && getStatusOptions.length > 0) {
      const statusMatch = getStatusOptions.find(
        (opt) => opt.label === props.DefaultStatusFilter
      );
      if (statusMatch) {
        setFilterValueStrings({
          Status: statusMatch.value.toString(),
        });
        setActiveFilters(["Status"]);
        setFilterValues({
          Status: [{ label: statusMatch.label, value: statusMatch.value }],
        });
      }
    }
  }, [props.DefaultStatusFilter, getStatusOptions]);

  useEffect(() => {
    if (props.DefaultCompanyTypeFilter && getCompanyTypeOptions.length > 0) {
      const companyTypeMatch = getCompanyTypeOptions.find(
        (opt) => opt.label.trim() === props.DefaultCompanyTypeFilter
      );
  
      if (companyTypeMatch) {
        setFilterValueStrings({
          CompanyType: companyTypeMatch.value.toString(),
        });
        setActiveFilters(["CompanyType"]);
        setFilterValues({
          CompanyType: [
            { label: companyTypeMatch.label, value: companyTypeMatch.value },
          ],
        });
      }
    }
  }, [props.DefaultCompanyTypeFilter, getCompanyTypeOptions]);

  useEffect(() => {
  if (props.DefaultTaskId && getTaskKeyOptions.length > 0) {
    const taskKeyMatch = getTaskKeyOptions.find(
      (opt) => opt.value.toString() === props.DefaultTaskId.toString()
    );

    if (taskKeyMatch) {
      setFilterValueStrings({
        TaskKey: taskKeyMatch.value.toString(),
      });
      setActiveFilters(["TaskKey"]);
      setFilterValues({
        TaskKey: [{ label: taskKeyMatch.label, value: taskKeyMatch.value }],
      });
    }
  }
}, [props.DefaultTaskId, getTaskKeyOptions]);

  const fetchOptions = async (optionType) => {
    try {
      let response;
      let statusDetails = {
        UserId: localStorage.getItem("userId"),
      };
      if (optionType === "Status") {
        response = await axios.get(getApiBaseUrl() + "/statuslist", {
          params: statusDetails,
        });
      } else if (optionType === "Priority") {
        let dropdownDetails = {
          UserId: localStorage.getItem("userId"),
          IsForDropodown: 1,
        };
        response = await axios.get(getApiBaseUrl() + "/getAllPriorities", {
          params: dropdownDetails,
        });
      } else if (optionType === "AssignedTo") {
        let statusDetails = {
          UserId: localStorage.getItem("userId"),
        };
        response = await axios.get(getApiBaseUrl() + "/userlist", {
          params: statusDetails,
        });
      } else if (optionType === "Title") {
        let titleDetails = {
          UserId: localStorage.getItem("userId"),
        };
        response = await axios.get(getApiBaseUrl() + "/titlelist", {
          params: titleDetails,
        });
      } else if (optionType === "TaskKey") {
        let taskDetails = {
          UserId: localStorage.getItem("userId"),
          ByUser: 0,
        };
        response = await axios.get(getApiBaseUrl() + "/tasklist", {
          params: taskDetails,
        });
      } else if (optionType === "CompanyType") {
        let clientCompanyTypeDetails = {
          UserId: localStorage.getItem("userId")
        };
        response = await axios.get(getApiBaseUrl() + "/clientCompanyTypelist", {
          params: clientCompanyTypeDetails,
        });
      }

      if (response.status === 200) {
        setDropdownData(response.data.data[0], optionType);
      }
    } catch (error) {
      console.log(error);
    }
  };

  function setDropdownData(cct, optionType) {
    let allOpt = [];
    if (optionType === "Status") {
      allOpt = cct.map((p) => ({
        value: p.StatusId,
        label: p.StatusName,
        name: "Status",
        description: p.Discription,
        isFixed: false,
        isDisabled: false,
        groupId: null,
      }));
      setStatusOptions(allOpt);
    } else if (optionType === "Priority") {
      allOpt = cct.map((p) => ({
        value: p.PriorityId,
        label: p.PriorityName,
        name: "Priority",
        description: p.Discription,
        isFixed: false,
        isDisabled: false,
        groupId: null,
      }));
      setPriorityOptions(allOpt);
    } else if (optionType === "AssignedTo") {
      allOpt = cct.map((p) => ({
        value: p.UserId ?? p.Id,
        label: p.FullName,
        name: "AssignedTo",
        description: p.Discription,
        isFixed: false,
        isDisabled: false,
        groupId: null,
      }));
      setAssignedToOptions(allOpt);
    } else if (optionType === "Title") {
      allOpt = cct.map((p) => ({
        value: p.TitleId,
        label: p.Title,
        name: "Title",
        description: p.Discription,
        isFixed: false,
        isDisabled: false,
        groupId: null,
      }));
      setTitleOptions(allOpt);
    } else if (optionType === "TaskKey") {
      allOpt = cct.map((p) => ({
        value: p.TaskId,
        label: p.TaskKey,
        name: "TaskKey",
        description: p.Discription,
        isFixed: false,
        isDisabled: false,
        groupId: null,
      }));
      setTaskKeyOptions(allOpt);
    } else if (optionType === "CompanyType") {
        allOpt = cct.map((p) => ({
          value: p.ClientCompanyTypeId,
          label: p.ClientCompanyTypeName,
          name: "CompanyType",
          description: p.Description,
          isFixed: false,
          isDisabled: false,
          groupId: null,
        }));
        setCompanyTypeOptions(allOpt);
      }
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top d-flex align-items-center justify-content-between">
            <div className="search-set d-flex align-items-center">
              <div className="search-input d-flex align-items-center">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search"
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => onChangeSearch(e)}
                />
              </div>
              <Link
                className="ms-2"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                title="Filter By"
              >
                <Icons.Filter color="#0c4777" />
              </Link>
              <div className="dropdown-menu notes-menu dropdown-menu-end">
                <Link
                  onClick={(e) => onFilterByClick(e)}
                  name="Priority"
                  className="dropdown-item"
                >
                  Priority{" "}
                </Link>
                <Link
                  onClick={(e) => onFilterByClick(e)}
                  name="Status"
                  className="dropdown-item"
                >
                  Status
                </Link>
                <Link
                  onClick={(e) => onFilterByClick(e)}
                  name="AssignedTo"
                  className="dropdown-item"
                >
                  Assigned To
                </Link>
                <Link
                  onClick={(e) => onFilterByClick(e)}
                  name="Title"
                  className="dropdown-item"
                >
                  Title
                </Link>
                <Link
                  onClick={(e) => onFilterByClick(e)}
                  name="TaskKey"
                  className="dropdown-item"
                >
                  Task Key
                </Link>
                <Link
                  onClick={(e) => onFilterByClick(e)}
                  name="CompanyType"
                  className="dropdown-item"
                >
                  Company Type
                </Link>
                <Link
                  onClick={() => setShowFollowupRequired((prev) => !prev)}
                  className="dropdown-item"
                >
                  {showFollowupRequired ? "Followup Require ✓" : "Followup Require"}
                </Link>
              </div>
              <div id="FilterContainer" className="filter-dropdown-container">
                {activeFilters.map((filterKey) => (
                  <div key={filterKey} className="filter-dropdown-wrapper">
                    {/* Dropdown */}
                    <MultiSelectDropdown
                      key={filterKey}
                      Options={
                        filterKey === "Priority"
                          ? getPriorityOptions
                          : filterKey === "Status"
                          ? getStatusOptions
                          : filterKey === "AssignedTo"
                          ? getAssignedToOptions
                          : filterKey === "Title"
                          ? getTitleOptions
                          : filterKey === "CompanyType"
                          ? getCompanyTypeOptions
                          : getTaskKeyOptions
                      }
                      filterKey={filterKey}
                      showLabel={true}
                      placeholder={
                        filterKey === "Priority"
                          ? "Select Priority"
                          : filterKey === "Status"
                          ? "Select Status"
                          : filterKey === "AssignedTo"
                          ? "Select Assignee"
                          : filterKey === "Title"
                          ? "Select Title"
                          : filterKey === "CompanyType"
                          ? "Select Company Type"
                          : "Select Task Key"
                      }
                      onChangeDropdown={(e) =>
                        handleFilterSelected(e, filterKey)
                      }
                      selectedValue={filterValues[filterKey] || []}
                    />

                    {/* Remove (X) icon */}
                    <span
                      className="remove-icon"
                      onClick={() => {
                        // Remove filter from activeFilters
                        setActiveFilters((prev) =>
                          prev.filter((key) => key !== filterKey)
                        );

                        // Remove the corresponding entry from filterValueStrings
                        setFilterValueStrings((prev) => {
                          const updated = { ...prev };
                          delete updated[filterKey];
                          return updated;
                        });

                        setFilterValues((prev) => {
                          const updated = { ...prev };
                          delete updated[filterKey];
                          return updated;
                        });
                      }}
                    >
                      ✕
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showCompletedCheckbox"
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="showCompletedCheckbox" style={{ marginBottom: 0 }}>
                  Show Completed
                </label>
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={filteredRows}
            pagination
            sortIcon={<SortArrow />}
            persistTableHead={true}
            expandableRows
            expandableRowsComponent={TaskingCommentHistory}
            onRowExpandToggled={handleRowExpandToggled}
            expandableRowExpanded={isRowExpanded}
            expandableRowsComponentProps={{ ref: commentRef, onNextFollowupUpdate: handleNextFollowupUpdate, showFollowupTab: showFollowupTab }}
          />
        </div>
      </div>
    </>
  );
}

export default ManageTaskGrid;
